import { useEffect, useRef, useState } from 'react';
import { useSimulationStore } from '@/store/simulationStore';

export interface TrendGraphProps {
  deviceId: string;
}

const MAX_POINTS = 100;

export function TrendGraph({ deviceId }: TrendGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [data, setData] = useState<{ time: number, sp: number, pv: number, cv: number }[]>([]);
  const lastTimeRef = useRef<number>(0);

  useEffect(() => {
    // Clear data when device changes
    setData([]);
    lastTimeRef.current = 0;
  }, [deviceId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // We can't rely on React state updates for 60fps graph drawing without performance issues,
    // so we'll use a requestAnimationFrame loop that reads from the store directly.
    let animationFrameId: number;

    const renderLoop = () => {
      const state = useSimulationStore.getState();
      const process = state.engine.process;
      const device = process.getDevice(deviceId);

      if (device && device.type === 'pid-controller') {
        const time = state.engine.simulationTimeMs;
        
        // Handle simulation reset (time < lastTimeRef)
        if (time < lastTimeRef.current) {
          lastTimeRef.current = time;
          setData([]);
        }

        // Only sample occasionally or if time advanced
        if (time > lastTimeRef.current) {
          lastTimeRef.current = time;
          
          setData(prev => {
            const newData = [...prev, {
              time,
              sp: Number(device.state['sp'] || 0),
              pv: Number(device.state['pv'] || 0),
              cv: Number(device.state['cv'] || 0)
            }];
            if (newData.length > MAX_POINTS) {
              return newData.slice(newData.length - MAX_POINTS);
            }
            return newData;
          });
        }
      }

      animationFrameId = requestAnimationFrame(renderLoop);
    };

    animationFrameId = requestAnimationFrame(renderLoop);

    return () => cancelAnimationFrame(animationFrameId);
  }, [deviceId]);

  // Draw loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background
    ctx.fillStyle = '#1e1e1e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (data.length < 2) return;

    const minTime = data[0]?.time ?? 0;
    const maxTime = data[data.length - 1]?.time ?? 0;
    const timeRange = Math.max(maxTime - minTime, 1000); // At least 1 second range

    // Draw grid
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
        const y = (i / 4) * canvas.height;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
    }

    const drawLine = (key: 'sp' | 'pv' | 'cv', color: string) => {
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      for (let i = 0; i < data.length; i++) {
        const point = data[i];
        if (!point) continue;
        const x = ((point.time - minTime) / timeRange) * canvas.width;
        
        // For PV and SP, use standard 0-100 (or maxLevel), we'll assume 0-100 range for now for visualization, or dynamically scale.
        // Let's use 0-100 scale for everything on the graph, assuming 0-100L or 0-100%.
        const maxValue = 100;
        const y = canvas.height - ((point[key] / maxValue) * canvas.height);
        
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };

    drawLine('sp', '#3b82f6'); // Blue
    drawLine('pv', '#22aa22'); // Green
    drawLine('cv', '#eab308');  // Yellow (0-100%)

  }, [data]);

  return (
    <div style={{ marginTop: '20px', padding: '10px', backgroundColor: 'var(--color-surface-elevated)', borderRadius: '8px' }}>
      <h3 style={{ fontSize: '12px', margin: '0 0 10px 0', color: 'var(--color-text-secondary)' }}>Live Trend</h3>
      <canvas ref={canvasRef} width={240} height={120} style={{ width: '100%', display: 'block', borderRadius: '4px' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '10px' }}>
         <span style={{ color: '#3b82f6' }}>SP</span>
         <span style={{ color: '#22aa22' }}>PV</span>
         <span style={{ color: '#eab308' }}>CV</span>
      </div>
    </div>
  );
}
